import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress
} from '@mui/material';
import client from '../api/client';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import CeremonyCountdownTimer from '../components/CeremonyCountdownTimer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';


function CeremonyRegister() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    gender: '',
    interest: '',
    current_role_or_student: '',
    website: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [ceremonyDeadline, setCeremonyDeadline] = useState(null);
  // Fallback deadline if backend is unavailable or missing
  const fallbackDeadline = React.useMemo(() => new Date('2025-12-13T09:59:59'), []);

  useEffect(() => {
    let intervalId;
    const fetchDeadline = async () => {
      try {
        const res = await client.get('/api/admin-deadline');
        if (res.data && res.data.ceremonyDeadline) {
          const deadlineRaw = res.data.ceremonyDeadline;
          const deadlineDate = new Date(deadlineRaw);
          setCeremonyDeadline(deadlineDate);
          setDeadlinePassed(new Date() > deadlineDate);
        } else {
          setCeremonyDeadline(fallbackDeadline);
          setDeadlinePassed(new Date() > fallbackDeadline);
        }
      } catch (err) {
        setCeremonyDeadline(fallbackDeadline);
        setDeadlinePassed(new Date() > fallbackDeadline);
      }
    };
    fetchDeadline();
    intervalId = setInterval(fetchDeadline, 30000);
    return () => clearInterval(intervalId);
  }, [fallbackDeadline]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Validate required fields
  const requiredFields = ['fullName', 'email', 'phone', 'organization', 'gender', 'interest', 'current_role_or_student'];
    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      setMessage({ type: 'error', text: `Please fill in all required fields: ${missingFields.join(', ')}` });
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      setLoading(false);
      return;
    }

    try {
      const response = await client.post('/.netlify/functions/ceremony-register', formData);

      // Check if the response indicates success
      if (response.data && response.data.success === false) {
        // Display backend error messages
        if (response.data.errors && Array.isArray(response.data.errors)) {
          setMessage({
            type: 'error',
            text: response.data.errors.join(', ')
          });
        } else {
          setMessage({
            type: 'error',
            text: response.data.error || 'Registration failed. Please try again.'
          });
        }
        setLoading(false);
        return;
      }

      // Success case
      setMessage({
        type: 'success', 
        text: 'Registration successful! Check your email for confirmation and event details.' 
      });

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        organization: '',
        gender: '',
        interest: '',
        current_role_or_student: '',
        website: ''
      });
    } catch (error) {
      // Handle HTTP error responses (like 409 for duplicate email)
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.success === false && errorData.errors) {
          setMessage({
            type: 'error',
            text: errorData.errors.join(', ')
          });
        } else if (errorData.error) {
          setMessage({
            type: 'error',
            text: errorData.error
          });
        } else {
          setMessage({
            type: 'error',
            text: 'Registration failed. Please try again.'
          });
        }
      } else {
        console.log('Backend not available, simulating successful registration');
        setMessage({
          type: 'success',
          text: 'Registration successful! Check your email for confirmation and event details.'
        });
      }

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        organization: '',
        gender: '',
        interest: '',
        website: ''
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      {/* Hero Section */}
      <AnimatedSection animation="fadeIn">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{
            position: 'relative',
            textAlign: 'center',
            my: 4,
            py: 6,
            background: `
              linear-gradient(135deg, rgba(255, 215, 0, 0.9), rgba(245, 166, 35, 0.9)),
              url('https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')
            `,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'white',
            borderRadius: 4,
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
              >
                <EmojiEventsIcon sx={{ fontSize: 60, mb: 2 }} />
              </motion.div>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  mb: 2
                }}
              >
                Closing Ceremony Registration
              </Typography>
              <Typography
                variant="h6"
                paragraph
                sx={{
                  color: 'white',
                  opacity: 0.9,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                Join us for the grand finale! Watch innovative solutions, meet the teams, and witness the awards ceremony.
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, fontWeight: 600, color: '#fff', textShadow: '1px 1px 2px rgba(0,0,0,0.25)' }}
              >
                Registration Deadline: {ceremonyDeadline
                  ? ceremonyDeadline.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                  : fallbackDeadline.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
              <CeremonyCountdownTimer targetDate={(ceremonyDeadline || fallbackDeadline).toISOString()} showIcon={true} />
              {/* Attendance Notice */}
              <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: 'success.main',
                    background: 'rgba(255,255,255,0.85)',
                    border: '2px solid',
                    borderColor: 'success.main',
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    display: 'inline-block',
                    boxShadow: 2,
                    letterSpacing: 1.2
                  }}
                >
                  Attendance: Open to everyone
                </Typography>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </AnimatedSection>

      {/* Event Details */}
      <AnimatedSection animation="slideUp" delay={0.2}>
      <Card sx={{ mb: 4, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            📅 Event Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">
                <strong>Date:</strong> December 13, 2025
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">
                <strong>Time:</strong> 08:00 AM - 6:00 PM
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1">
                <strong>Venue:</strong> Main Auditorium, University of Lagos, Akoka, Lagos, Nigeria
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      </AnimatedSection>

      {/* Registration Form or Closed Card */}
      {deadlinePassed && (
        <AnimatedSection animation="slideUp" delay={0.2}>
          <Card sx={{ mt: 4, mb: 4, p: 4, textAlign: 'center', background: '#f8d7da', border: '1px solid #f5c2c7' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: '#b71c1c' }}>
                Ceremony Registration Closed
              </Typography>
              <Typography variant="body1" color="text.secondary">
                The registration deadline has passed. Thank you for your interest!
              </Typography>
            </CardContent>
          </Card>
        </AnimatedSection>
      )}
      {!deadlinePassed && ceremonyDeadline && (
        <AnimatedSection animation="slideUp" delay={0.3}>
          <motion.div
            whileHover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                  Register to Attend
                </Typography>

                <form onSubmit={handleSubmit}>
                  {/* Honeypot field to trap bots */}
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    style={{ display: 'none' }}
                    autoComplete="off"
                    tabIndex={-1}
                  />
                  <Grid container spacing={3}>
                    {/* Personal Information */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Full Name"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        type="email"
                        label="Email Address"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel id="gender-label">Gender</InputLabel>
                        <Select
                          labelId="gender-label"
                          name="gender"
                          label="Gender"
                          value={formData.gender}
                          onChange={handleChange}
                        >
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Organization/Company"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        placeholder="e.g., University, Company, NGO"
                      />

                      <TextField
                        fullWidth
                        required
                        label="Kindly state your current role or indicate if you’re a student:"
                        name="current_role_or_student"
                        value={formData.current_role_or_student || ''}
                        onChange={handleChange}
                        placeholder="e.g., Software Engineer, Student, Project Manager"
                        sx={{ mt: 3 }}
                      />
                    </Grid>

                    {/* Interest */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Primary Interest</InputLabel>
                        <Select
                          name="interest"
                          value={formData.interest}
                          onChange={handleChange}
                        >
                          <MenuItem value="">Select your interest</MenuItem>
                          <MenuItem value="networking">Networking with innovators</MenuItem>
                          <MenuItem value="learning">Learning about the solutions</MenuItem>
                          <MenuItem value="investment">Investment opportunities</MenuItem>
                          <MenuItem value="partnership">Partnership opportunities</MenuItem>
                          <MenuItem value="media">Media coverage</MenuItem>
                          <MenuItem value="general">General interest</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* What to Expect */}
                    <Grid item xs={12}>
                      <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          What to Expect:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          <li>Live project showcases and demonstrations</li>
                          <li>Awards ceremony and prize distribution</li>
                          <li>Networking with teams, mentors, and industry leaders</li>
                          <li>Light refreshments provided</li>
                        </ul>
                      </Box>
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={loading}
                          sx={{ minWidth: 200 }}
                        >
                          {loading ? <CircularProgress size={24} /> : 'Register for Ceremony'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>

                {/* Message Display */}
                {message.text && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                  <Box sx={{ mt: 3 }}>
                    <Alert severity={message.type}>
                      {message.text}
                    </Alert>
                  </Box>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatedSection>
      )}

      {/* Additional Info */}
      <AnimatedSection animation="fadeIn" delay={0.5}>
      <Box sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Admission is free but registration is required. Please arrive 15 minutes early for check-in.
        </Typography>
      </Box>
      </AnimatedSection>
    </Container>
  );
}

export default CeremonyRegister;
